import {
  IMixpanelEventProperties,
  MixpanelEventType,
} from 'src/app/core/services/mixpanel-service/mixpanel.service';
import { Features } from 'src/app/features/features';
import { ReportsSubFeatures } from 'src/app/features/reports/models/sub-features';
import { SettingsSubFeatures } from 'src/app/features/settings/models/sub-features';
import {
  AgencyDocsPermissions,
  AppetiteSearchPermissions,
  BillingPermissions,
  CQSPermissions,
  CarrierEnrollmentsPermissions,
  CarrierSettingPermissions,
  CommissionsPermissions,
  CommissionsReportPermission,
  CompliancePermissions,
  ExchangePermissions,
  ExchangeReportPermission,
  HelpCenterPermissions,
  QuoteCreatePermissions,
  QuotesReportPermission,
  RecommendedCarriersIndexBucketPermissions,
  ReportsPermissions,
  StorefrontPermissions,
  UserPermissions,
  WebhookAdminPermissions,
} from 'src/app/permissions/permissions';
import {
  IPermissionModelForRouteAndMenu,
  ProductFeatureType,
} from 'src/app/permissions/permissions.model';
import * as QuotesActions from '../../features/quotes/store/quotes.actions';
import { ActionCreator } from '@ngrx/store';

// Note: For permissions, a model refers to a resource string, and an
//       action refers to an operation the user could perform on
//       the model (index, create, etc)
interface IMenuItem extends Partial<IPermissionModelForRouteAndMenu> {
  url?: string[] | null;
  text: string;
  subscriptionRequired?: boolean;
  icon?: string;
  disabled?: boolean;
  subItems?: IMenuItem[];
  isNavButton?: boolean;
  action?: ActionCreator<string, any>;
  mixpanelCallback?: {
    event: MixpanelEventType;
    props?: IMixpanelEventProperties;
  };
  externalUrl?: string;
}

const allMainMenuItems: IMenuItem[] = [
  {
    text: 'Home',
    url: [Features.QUOTES],
    icon: 'bp_document_shield',
  },

  {
    text: 'Create New Application',
    url: [Features.QUOTES, 'start'],
    icon: 'bp_add_circle',
    subscriptionRequired: true,
    isNavButton: true,
    ...QuoteCreatePermissions,
    productFeatureDisabled: [
      ProductFeatureType.UseDocumentUpload,
      ProductFeatureType.SubmissionLinkSubmit,
    ],
  },
  {
    text: 'New Application',
    url: null,
    icon: 'bp_document_add',
    subscriptionRequired: true,
    ...QuoteCreatePermissions,
    productFeatureEnabled: [
      ProductFeatureType.UseDocumentUpload,
      ProductFeatureType.SubmissionLinkSubmit,
    ],
    subItems: [
      {
        text: 'Upload Document',
        url: null,
        action: QuotesActions.RequestApplicationUploadDialog,
        productFeatureEnabled: [ProductFeatureType.UseDocumentUpload],
      },
      {
        text: 'Smart Upload Document',
        url: null,
        action: QuotesActions.TriggerApplicationSmartUploadDialog,
        productFeatureEnabled: [ProductFeatureType.SubmissionLinkSubmit],
      },
      {
        text: 'Create with Wingman',
        isNavButton: true,
        url: null,
        action: QuotesActions.CreateWingmanApplicationFormAction,
        productFeatureEnabled: [ProductFeatureType.TerminalWingmanSession],
      },
      {
        text: 'Create New Application',
        isNavButton: true,
        url: [Features.QUOTES, 'start'],
      },
    ],
  },
  {
    text: 'Client Quote-Start',
    url: [Features.SETTINGS, SettingsSubFeatures.CLIENT_QUOTE_START],
    icon: 'bp_user_upload',
    ...CQSPermissions,
  },
  {
    text: 'Storefront',
    url: [Features.SETTINGS, SettingsSubFeatures.STOREFRONT_SETTINGS],
    icon: 'bp_user_group',
    ...StorefrontPermissions,
  },
  {
    text: 'Appetite Search',
    url: [Features.APPETITE_SEARCH],
    icon: 'bp_page_search',
    ...AppetiteSearchPermissions,
  },
  {
    text: 'Exchange',
    url: [Features.EXCHANGE],
    icon: 'bp_store',
    ...ExchangePermissions,
  },
  {
    text: 'Insights',
    url: [Features.REPORTS],
    icon: 'bp_document_analytics_up',
    ...ReportsPermissions,
    subItems: [
      {
        text: 'Quotes',
        url: [Features.REPORTS, ReportsSubFeatures.QUOTES],
        ...QuotesReportPermission,
      },
      {
        text: 'Commissions',
        url: [Features.REPORTS, ReportsSubFeatures.COMMISSIONS],
        ...CommissionsReportPermission,
      },
      {
        text: 'Exchange',
        url: [Features.REPORTS, ReportsSubFeatures.EXCHANGE],
        ...ExchangeReportPermission,
      },
    ],
  },
];

const allSettingsMenuItems: IMenuItem[] = [
  {
    text: 'Terminal Configuration',
    url: [Features.TERMINAL_CONFIGURATION],
    icon: 'bp_settings_tools',
    subItems: [
      {
        text: 'Carrier Appointments',
        url: [
          Features.TERMINAL_CONFIGURATION,
          SettingsSubFeatures.CARRIER_APPOINTMENTS,
        ],
        ...CarrierEnrollmentsPermissions,
      },
      {
        text: 'Carrier Settings',
        url: [
          Features.TERMINAL_CONFIGURATION,
          SettingsSubFeatures.CARRIER_SETTINGS,
        ],
        ...CarrierSettingPermissions,
      },
      {
        text: 'Recommended Carriers',
        url: [
          Features.TERMINAL_CONFIGURATION,
          SettingsSubFeatures.RECOMMENDED_CARRIERS,
        ],
        ...RecommendedCarriersIndexBucketPermissions,
      },
      {
        text: 'User Management',
        url: [Features.TERMINAL_CONFIGURATION, SettingsSubFeatures.USERS],
        ...UserPermissions,
      },
      {
        text: 'Webhook Settings',
        url: [Features.TERMINAL_CONFIGURATION, SettingsSubFeatures.WEBHOOKS],
        ...WebhookAdminPermissions,
      },
    ],
  },
  {
    text: 'Agency Information',
    url: [Features.AGENCY_INFORMATION],
    icon: 'bp_shield_user',
    subItems: [
      {
        text: 'Agency Documents',
        url: [
          Features.AGENCY_INFORMATION,
          SettingsSubFeatures.AGENCY_DOCUMENTS,
        ],
        ...AgencyDocsPermissions,
      },
      {
        text: 'Commissions',
        url: [Features.AGENCY_INFORMATION, SettingsSubFeatures.COMMISSIONS],
        ...CommissionsPermissions,
      },
      {
        text: 'Compliance',
        url: [Features.AGENCY_INFORMATION, SettingsSubFeatures.COMPLIANCE],
        ...CompliancePermissions,
      },
      {
        text: 'Billing',
        url: [Features.AGENCY_INFORMATION, SettingsSubFeatures.BILLING],
        ...BillingPermissions,
      },
    ],
  },
  {
    text: 'Help Center',
    externalUrl: 'https://intercom.help/pinguino-audaz/en',
    icon: 'bp_information_circle',
    ...HelpCenterPermissions,
  },
];

export { IMenuItem, allMainMenuItems, allSettingsMenuItems };
